import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { TodoListMember, UserTodoItem } from '@dreamai/types';
import { ReactComponent as DreamLogo } from '../../icons/dream-logo-compact.svg';
import { ReactComponent as CalendarOutlineIcon } from '../../icons/calendar-outline.svg';
import { ReactComponent as PlusAddIcon } from '../../icons/plus-add.svg';
import MemberAvatar from '../chat/reminders/MemberAvatar';
import { describeTodoProvenance } from '../chat/reminders/todoProvenance';
import { formatDueDate, isDueDateOverdue } from '../../utils/dueDate';
import {
  fetchGuestList,
  GuestApiError,
  setGuestTodoStatus,
  TodoListGuestVerificationInfo,
  TodoListGuestViewResponse,
} from './guestApi';
import GuestNudgeModal from './GuestNudgeModal';
import GuestNewTaskSheet from './GuestNewTaskSheet';
import GuestVerifyScreen from './GuestVerifyScreen';
import '../chat/reminders-page.scss';
import '../chat/reminders/assignee-picker.scss';
import PublicNoticePage from '../common/PublicNoticePage';
import './todo-list-guest.scss';

/** Fallback when the API gave no WhatsApp link (e.g. it was unreachable). */
const WAITLIST_URL = 'https://usedream.ai/join-pa';

const PRIORITY_COLORS: Record<string, string> = {
  high: '#EF4444',
  medium: '#F59E0B',
  low: '#3B82F6',
};

const UI_LOCALES: Record<string, string> = {
  en: 'en-US',
  fr: 'fr-FR',
  de: 'de-DE',
  es: 'es-ES',
  it: 'it-IT',
};

type GuestView = Required<
  Pick<
    TodoListGuestViewResponse,
    'list' | 'inviter' | 'guest' | 'members' | 'todos'
  >
>;

const acceptedMembers = (members: TodoListMember[]) =>
  members.filter((m) => m.status === 'accepted');

/**
 * Public guest view of a shared to-do list, reached from the invite link
 * (`/to-do-list/guest/:token`). Guests are not on Dream: they see the list,
 * can add tasks to it, and may tick off (or un-tick) tasks assigned to them or
 * added by them. The first completion opens the "get your own PA" nudge. The
 * layout is the mobile Reminders body (Figma 5249:1205); on wider screens the
 * same column is scaled up and centred rather than switching to the
 * authenticated desktop UI.
 *
 * A browser that has not been seen for this link is asked for the one-time
 * code sent to the invited address first (`GuestVerifyScreen`); the API
 * answers 401 until it has, and again if the device is later revoked.
 */
const TodoListGuestPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const locale = UI_LOCALES[i18n.language] ?? 'en-US';

  const [view, setView] = useState<GuestView | null>(null);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [signupUrl, setSignupUrl] = useState<string | null>(null);
  const [verification, setVerification] =
    useState<TodoListGuestVerificationInfo | null>(null);
  const [loadKey, setLoadKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [nudgeShown, setNudgeShown] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);

  useEffect(() => {
    document.title = t('todoGuest.pageTitle');
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setInvalid(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchGuestList(token)
      .then((res) => {
        if (cancelled) return;
        if (res.list && res.inviter && res.guest && res.members && res.todos) {
          setVerification(null);
          setView({
            list: res.list,
            inviter: res.inviter,
            guest: res.guest,
            members: res.members,
            todos: res.todos,
          });
        } else {
          setInvalid(true);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof GuestApiError && err.verification) {
          setVerification(err.verification);
          return;
        }
        if (err instanceof GuestApiError && err.whatsappUrl) {
          setSignupUrl(err.whatsappUrl);
        }
        setInvalid(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, loadKey]);

  const reload = useCallback(() => setLoadKey((k) => k + 1), []);

  const markInvalid = useCallback((whatsappUrl?: string) => {
    if (whatsappUrl) setSignupUrl(whatsappUrl);
    setVerification(null);
    setInvalid(true);
  }, []);

  /** A protected call was refused: dead link, or this device got revoked. */
  const handleAccessError = useCallback(
    (err: unknown): boolean => {
      if (!(err instanceof GuestApiError)) return false;
      if (err.verification) {
        setView(null);
        setVerification(err.verification);
        return true;
      }
      if (err.status === 404) {
        markInvalid(err.whatsappUrl);
        return true;
      }
      return false;
    },
    [markInvalid],
  );

  const guestUserId = view?.guest.userId;
  const isMine = useCallback(
    (todo: UserTodoItem) =>
      !!guestUserId &&
      (todo.assigneeUserId === guestUserId ||
        todo.createdByUserId === guestUserId),
    [guestUserId],
  );
  const { pending, completedByGuest } = useMemo(() => {
    const todos = view?.todos ?? [];
    return {
      pending: todos.filter((todo) => todo.status !== 'complete'),
      completedByGuest: todos.filter(
        (todo) => todo.status === 'complete' && isMine(todo),
      ),
    };
  }, [view, isMine]);

  const addCreated = useCallback((todo: UserTodoItem) => {
    setView((current) =>
      current ? { ...current, todos: [todo, ...current.todos] } : current,
    );
  }, []);

  const toggle = useCallback(
    async (todo: UserTodoItem) => {
      if (!view || busyId || !isMine(todo)) return;
      const next = todo.status === 'complete' ? 'pending' : 'complete';
      setBusyId(todo.id);
      setActionError(null);
      try {
        const res = await setGuestTodoStatus(token, todo.id, next);
        const updated = res.todo ?? { ...todo, status: next };
        setView((current) =>
          current
            ? {
                ...current,
                todos: current.todos.map((item) =>
                  item.id === todo.id ? updated : item,
                ),
              }
            : current,
        );
        if (next === 'complete' && !nudgeShown) {
          setNudgeShown(true);
          setNudgeOpen(true);
        }
      } catch (err) {
        if (!handleAccessError(err)) {
          setActionError(t('todoGuest.errors.updateFailed'));
        }
      } finally {
        setBusyId(null);
      }
    },
    [view, busyId, isMine, token, nudgeShown, t, handleAccessError],
  );

  const renderTodo = (todo: UserTodoItem) => {
    const mine = isMine(todo);
    const assignedToGuest = todo.assigneeUserId === view?.guest.userId;
    const done = todo.status === 'complete';
    const provenance = describeTodoProvenance(todo, view?.guest.userId);
    const circleLabel = !mine
      ? t('todoGuest.task.notYours')
      : done
        ? t('todoGuest.task.reopen')
        : t('todoGuest.task.complete');

    return (
      <div
        key={todo.id}
        className={`reminder-item guest-reminder-item${mine ? ' guest-reminder-item--mine' : ''}${done ? ' guest-reminder-item--done' : ''}`}
        data-reminder-id={todo.id}
      >
        <button
          type='button'
          className='reminder-circle'
          onClick={() => toggle(todo)}
          disabled={!mine || busyId === todo.id}
          aria-label={circleLabel}
          title={circleLabel}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <circle
              cx='12'
              cy='12'
              r='11.5'
              fill={done ? '#489FD9' : '#FFFEFC'}
              stroke={done ? '#489FD9' : '#C1C5CD'}
            />
            {done && (
              <path
                className='tick'
                d='M7 12L10.5 15.5L17 9'
                stroke='white'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                fill='none'
              />
            )}
          </svg>
        </button>

        <div className='reminder-mid'>
          <p className='reminder-description'>
            {todo.priority && PRIORITY_COLORS[todo.priority] && (
              <span
                className='priority-dot'
                style={{ background: PRIORITY_COLORS[todo.priority] }}
              />
            )}
            {todo.description}
          </p>
          <div className='reminder-meta'>
            {todo.dueDate && (
              <span
                className={`reminder-due${isDueDateOverdue(todo.dueDate) && !done ? ' overdue' : ''}`}
              >
                <CalendarOutlineIcon className='calendar-icon' />
                {formatDueDate(todo.dueDate, { locale })}
              </span>
            )}
            <span className='reminder-people'>
              {provenance.addedBy && (
                <span className='reminder-creator'>
                  {t(
                    provenance.addedBy.guest
                      ? 'todoGuest.task.fromGuest'
                      : 'todoGuest.task.from',
                    { name: provenance.addedBy.name },
                  )}
                </span>
              )}
              {provenance.editedBy && (
                <span className='reminder-creator reminder-edited'>
                  {t('todoGuest.task.editedBy', { name: provenance.editedBy })}
                </span>
              )}
              <span
                className={`guest-assignee${todo.assigneeName ? ' has-assignee' : ''}${assignedToGuest ? ' is-guest' : ''}`}
              >
                {todo.assigneeName && (
                  <MemberAvatar name={todo.assigneeName} size={16} />
                )}
                {assignedToGuest
                  ? t('todoGuest.task.assignedToYou')
                  : (todo.assigneeName ?? t('todoGuest.task.unassigned'))}
              </span>
            </span>
          </div>
        </div>
      </div>
    );
  };

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className='guest-skeleton' aria-busy='true'>
        <div className='guest-skeleton-banner' />
        <div className='guest-skeleton-title' />
        <div className='guest-skeleton-row' />
        <div className='guest-skeleton-row' />
        <div className='guest-skeleton-row' />
      </div>
    );
  } else if (!invalid && verification) {
    body = (
      <GuestVerifyScreen
        key={token}
        token={token}
        info={verification}
        onVerified={reload}
        onInvalid={markInvalid}
      />
    );
  } else if (invalid || !view) {
    return (
      <PublicNoticePage
        testId='guest-invalid'
        title={t('todoGuest.invalid.title')}
        body={t('todoGuest.invalid.body')}
        actions={
          <a
            className='public-notice__button'
            href={signupUrl ?? WAITLIST_URL}
            target='_blank'
            rel='noopener noreferrer'
          >
            {t('todoGuest.invalid.cta')}
          </a>
        }
      />
    );
  } else {
    const members = acceptedMembers(view.members);
    body = (
      <>
        <div className='guest-banner' data-testid='guest-banner'>
          <p>
            <Trans
              i18nKey='todoGuest.banner'
              values={{ inviter: view.inviter.name, list: view.list.name }}
              components={{ strong: <strong /> }}
            />
          </p>
        </div>

        <main className='reminders-page reminders-page--mobile guest-main'>
          <div className='guest-header'>
            <h2 className='guest-title'>{view.list.name}</h2>
            <button
              type='button'
              className='reminders-new-btn guest-new-btn'
              onClick={() => setNewTaskOpen(true)}
              data-testid='guest-new-task'
            >
              <PlusAddIcon />
              <span>{t('todoGuest.newTask.button')}</span>
            </button>
            <div className='guest-members'>
              <span>{t('todoGuest.members', { count: members.length })}</span>
              <span className='guest-members-avatars'>
                {members.slice(0, 5).map((m) => (
                  <MemberAvatar
                    key={m.userId}
                    name={m.name}
                    size={24}
                    title={
                      m.userId === view.guest.userId
                        ? t('todoGuest.you', { name: m.name })
                        : m.name
                    }
                  />
                ))}
              </span>
            </div>
          </div>

          {actionError && (
            <p className='guest-action-error' role='alert'>
              {actionError}
            </p>
          )}

          <div className='reminders-list' data-testid='guest-todos'>
            {pending.length === 0 && completedByGuest.length === 0 ? (
              <div className='reminders-empty guest-empty'>
                <p>{t('todoGuest.empty')}</p>
              </div>
            ) : (
              pending.map(renderTodo)
            )}
          </div>

          {completedByGuest.length > 0 && (
            <section className='guest-completed' data-testid='guest-completed'>
              <h3>{t('todoGuest.completedByYou')}</h3>
              <div className='reminders-list'>
                {completedByGuest.map(renderTodo)}
              </div>
            </section>
          )}
        </main>

        <GuestNewTaskSheet
          open={newTaskOpen}
          onClose={() => setNewTaskOpen(false)}
          token={token}
          guestUserId={view.guest.userId}
          members={members}
          onCreated={addCreated}
          onAccessError={handleAccessError}
        />

        <GuestNudgeModal
          open={nudgeOpen}
          onClose={() => setNudgeOpen(false)}
          token={token}
          channel={view.guest.channel}
        />
      </>
    );
  }

  return (
    <div className='todo-guest-page' data-testid='todo-guest-page'>
      <div className='todo-guest-frame'>
        <header className='todo-guest-topbar'>
          <a
            href='https://usedream.ai'
            aria-label='Dream'
            className='todo-guest-logo'
          >
            <DreamLogo />
          </a>
        </header>
        <div className='todo-guest-body'>{body}</div>
      </div>
    </div>
  );
};

export default TodoListGuestPage;
